import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Star, Quote, UserCircle } from 'lucide-react';

const testimonials = [
  {
    name: 'Anne',
    location: 'Nairobi',
    role: 'Tenant',
    text: "I used to argue with my landlord about deposits every time. With YAWOTE, everything is documented with photos. I got my full deposit back for the first time in 5 years.",
    gradient: 'from-blue-500 to-cyan-500',
    rating: 5,
  },
  {
    name: 'James',
    location: 'Mombasa',
    role: 'Property Manager',
    text: 'Delinquency was eating into my profits. YAWOTE automated collections and reduced late payments from 40% to 8%. Best decision ever for my portfolio.',
    gradient: 'from-purple-500 to-pink-500',
    rating: 5,
  },
  {
    name: 'Sarah',
    location: 'Kisumu',
    role: 'Tenant',
    text: "The community feature is amazing. I got advice on finding reliable electricians and saved thousands. It's like having friends in every building.",
    gradient: 'from-emerald-500 to-green-500',
    rating: 5,
  },
];

const stats = [
  { value: '4.9★', label: 'App Store Rating' },
  { value: '98%', label: 'Satisfaction Rate' },
  { value: '12M+', label: 'Active Users' },
];

export default function TestimonialSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const bgOrb1Y = useTransform(scrollYProgress, [0, 1], ['0%', '-20%']);
  const bgOrb2Y = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
  const [activeIndex, setActiveIndex] = React.useState(0);

  return (
    <section ref={ref} className="py-24 relative overflow-hidden">
      {/* Background with parallax */}
      <motion.div className="absolute top-20 right-20 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" style={{ y: bgOrb1Y }}></motion.div>
      <motion.div className="absolute bottom-20 left-20 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" style={{ y: bgOrb2Y }}></motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <span className="inline-block px-4 py-2 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-sm font-medium mb-6">
            Trusted by millions
          </span>
          <h2 className="section-title">
            Real stories from{' '}
            <span className="text-gradient">real users</span>
          </h2>
          <p className="text-lg text-gray-300 mt-4">
            Trusted by millions across Africa
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {testimonials.map((testimonial, idx) => (
            <motion.div
              key={idx}
              className="card group flex flex-col"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.15 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star
                    key={i}
                    size={16}
                    className="text-yellow-400 fill-yellow-400"
                  />
                ))}
              </div>

              {/* Quote Icon */}
              <Quote className="text-blue-400/30 mb-3" size={28} />

              {/* Text */}
              <p className="text-gray-300 mb-6 flex-1 text-sm leading-relaxed italic">
                "{testimonial.text}"
              </p>

              {/* Divider */}
              <div className="border-t border-white/5 pt-5">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${testimonial.gradient} flex items-center justify-center ring-2 ring-blue-500/20`}>
                    <UserCircle className="text-white" size={24} />
                  </div>
                  <div>
                    <p className="font-bold text-white text-sm">{testimonial.name}</p>
                    <p className="text-gray-400 text-xs">
                      {testimonial.role} • {testimonial.location}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats Highlight */}
        <motion.div
          className="grid md:grid-cols-3 gap-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          viewport={{ once: true }}
        >
          {stats.map((stat, idx) => (
            <motion.div
              key={idx}
              className="text-center p-8 rounded-2xl bg-white/[0.02] border border-white/5"
              whileHover={{ scale: 1.02 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <div className="text-4xl font-bold text-gradient mb-2">{stat.value}</div>
              <p className="text-gray-400 text-sm">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
